import { TeamsApi } from '@up2stay/up2promo-api-client';
import { Button, Card, CardContent, cn } from '@up2stay/up2promo-ui';
import { Check, Users } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import { GridAddCard } from '@/core/components/layout/grid-add-card';
import {
  InputWithCharCount,
  TextareaWithCharCount,
} from '@/core/components/ui/field-with-char-count';
import {
  triggerErrorHaptic,
  triggerSuccessHaptic,
} from '@/core/lib/dnd-haptics';

import { TEAM_COLOR_PRESETS } from './team-color-presets';

const TEAM_NAME_MAX_LENGTH = 80;
const TEAM_DESCRIPTION_MAX_LENGTH = 500;

interface CreateTeamInlineCardProps {
  workspaceId: string;
  onCreated: () => void;
  dict: {
    addCard: string;
    namePlaceholder: string;
    descriptionPlaceholder: string;
    inlineSave: string;
    inlineCancel: string;
    successMessage: string;
    errorMessage: string;
    errorNameTaken: string;
  };
}

export function CreateTeamInlineCard({
  workspaceId,
  onCreated,
  dict,
}: CreateTeamInlineCardProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);

  const handleStartCreating = () => {
    setIsCreating(true);
    setName('');
    setDescription('');
    setColor('');
    requestAnimationFrame(() => nameInputRef.current?.focus());
  };

  const handleCancel = () => {
    setIsCreating(false);
    setName('');
    setDescription('');
    setColor('');
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      void triggerErrorHaptic();
      return;
    }

    setIsSaving(true);
    try {
      await TeamsApi.create({
        path: { workspaceId },
        body: {
          name: trimmedName,
          description: description.trim() || undefined,
          color: color || undefined,
        },
      });
      void triggerSuccessHaptic();
      toast.success(dict.successMessage);
      handleCancel();
      onCreated();
    } catch (err: unknown) {
      console.error('Failed to create team:', err);
      void triggerErrorHaptic();
      const isNameTaken =
        (err as { response?: { data?: { errors?: { name?: string } } } })
          ?.response?.data?.errors?.name === 'teamNameAlreadyExists';
      toast.error(isNameTaken ? dict.errorNameTaken : dict.errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isCreating) {
    return <GridAddCard label={dict.addCard} onClick={handleStartCreating} />;
  }

  return (
    <Card className="flex h-full flex-col border-[color:var(--ghost-accent-border)] bg-[color:var(--ghost-accent-bg)]/30 shadow-md">
      <CardContent className="flex flex-col gap-3 p-5 sm:p-6">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
          className="flex flex-col gap-3"
        >
          <div className="relative">
            <Users className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            {/* Same limits as the API (name 80, description 500) and the
                team detail, so the field stops instead of a 422 (DEV-821). */}
            <InputWithCharCount
              ref={nameInputRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={dict.namePlaceholder}
              className="h-10 pl-9 text-[13.5px] font-semibold"
              maxLength={TEAM_NAME_MAX_LENGTH}
              graphemeCount={false}
              valueLength={name.length}
              required
            />
          </div>

          <TextareaWithCharCount
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={dict.descriptionPlaceholder}
            rows={2}
            className="min-h-0 resize-none text-[13.5px] leading-relaxed"
            maxLength={TEAM_DESCRIPTION_MAX_LENGTH}
            graphemeCount={false}
            valueLength={description.length}
          />

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-1.5">
              {TEAM_COLOR_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  aria-pressed={color === preset}
                  onClick={() => setColor(preset)}
                  className={cn(
                    'size-6 rounded-full border-2 transition',
                    color === preset
                      ? 'scale-110 border-foreground'
                      : 'border-transparent hover:scale-105',
                  )}
                  style={{ backgroundColor: preset }}
                />
              ))}
              <button
                type="button"
                aria-pressed={!color}
                onClick={() => setColor('')}
                className={cn(
                  'h-6 rounded-full border px-1.5 text-xs',
                  !color ? 'border-foreground bg-muted' : 'border-input',
                )}
              >
                —
              </button>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <Button
                type="submit"
                size="sm"
                disabled={isSaving || !name.trim()}
                className="h-9 min-h-9"
              >
                <Check className="mr-1 h-3.5 w-3.5" />
                {dict.inlineSave}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                disabled={isSaving}
                className="h-9 min-h-9 px-2 text-muted-foreground"
              >
                {dict.inlineCancel}
              </Button>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
